import { localStorageAvailable } from 'minimal-shared/utils';
import {
  sum,
  limit,
  query,
  where,
  count,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  orderBy,
  average,
  deleteDoc,
  writeBatch,
  startAfter,
  collection,
  doc as _doc,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  CACHE_SIZE_UNLIMITED,
  getAggregateFromServer,
  increment as _increment,
  onSnapshot as _onSnapshot,
  persistentMultipleTabManager,
  serverTimestamp as _serverTimestamp,
} from 'firebase/firestore';

import { firebaseApp } from '../';

// ----------------------------------------------------------------------

export const FIRESTORE =
  typeof window !== 'undefined' && localStorageAvailable()
    ? initializeFirestore(firebaseApp, {
        localCache: persistentLocalCache({
          useFetchStreams: true,
          cacheSizeBytes: CACHE_SIZE_UNLIMITED,
          tabManager: persistentMultipleTabManager(),
        }),
      })
    : getFirestore(firebaseApp);

export const doc = _doc;
export const increment = _increment;
export const onSnapshot = _onSnapshot;
export const serverTimestamp = _serverTimestamp;
export const createBatch = (firestore) => writeBatch(firestore ?? FIRESTORE);

/**
 * Create data
 * @param {string} path example: 'users'
 * @param {object} data example: { name: 'Joe Doe' }
 * @returns {Promise} document data
 * @example
 * const document = await createData('users', { name: 'Joe Doe' });
 * console.log(document);
 * // { id: 'Xq2mB...', ref: firebase.firestore.DocumentReference, ...data }
 */
export async function createData(path, data, firestore) {
  try {
    const docRef = await addDoc(collection(firestore ?? FIRESTORE, path), data);
    return docRef;
  } catch (error) {
    console.error(error);
    return null;
  }
}

/**
 * Update data
 * @param {string} path example: 'users/Xq2mB...'
 * @param {object} data example: { name: 'Joe Doe Mod' }
 * @example updateData('users/Xq2mB...', { name: 'Joe Doe Mod' });
 */
export async function updateData(path, data, batch, firestore) {
  try {
    const pathRef =
      path.split('/').length % 2
        ? doc(collection(firestore ?? FIRESTORE, path))
        : doc(firestore ?? FIRESTORE, path);

    if (batch) {
      batch.set(pathRef, data, { merge: true });
    } else {
      await setDoc(pathRef, data, { merge: true });
    }
  } catch (error) {
    console.error(error);
  }
}

/**
 * Delete data
 * @param {string} path example: 'users/Xq2mB...'
 */
export async function deleteData(path, batch, firestore) {
  try {
    if (batch) {
      batch.delete(doc(firestore ?? FIRESTORE, path));
    } else {
      await deleteDoc(doc(firestore ?? FIRESTORE, path));
    }
  } catch (error) {
    console.error(error);
  }
}

export async function getDocData(path, defaultData, formatter, firestore) {
  try {
    const docData = await getDoc(doc(firestore ?? FIRESTORE, path));
    return docData.exists()
      ? {
          ...(await formatter({ id: docData.id, ...docData.data() })),
          id: docData.id,
          path: docData.ref.path,
        }
      : defaultData;
  } catch (error) {
    console.error(error);
    return defaultData;
  }
}

export async function getCollectionData(path, defaultData, formatter, queryConstraints, firestore) {
  try {
    const { docs } = await getDocs(
      query(collection(firestore ?? FIRESTORE, path), ...queryConstraints)
    );
    return docs.length
      ? Promise.all(
          docs.map(async (document) => ({
            ...(await formatter({ id: document.id, ...document.data() })),
            id: document.id,
            path: document.ref.path,
          }))
        )
      : defaultData;
  } catch (error) {
    console.error(error);
    return defaultData;
  }
}

/**
 * Get data from a document or collection
 * @param {{ path: string; options: object; defaultData: object|any; formatter: function; }} params
 * @param {string} params.path example: 'users/Xq2mB...' or 'users'.
 * @param {object} params.options Use only when consulting a collection. See example get collection data.
 * @param {object|any} params.defaultData default {}, example: {} or []
 * @param {function} params.formatter example: (data) => ({ ...data, name: data.name.toUpperCase()})
 * @example
 * // Get document data
 * const userData = await getData({
 *   path: 'users/Xq2mB...',
 *   formatter: (data) => ({ ...data, name: data.name.toUpperCase()}),
 * });
 * console.log(userData);
 * // { name: 'JOE DOE', ...data, id: 'Xq2mB...', path: 'users/Xq2mB...' }
 *
 * // Get collection data
 * const usersData = await getData({
 *   path: 'users',
 *   defaultData: [],
 *   formatter: (data) => ({ ...data, name: data.name.toUpperCase()}),
 *   options: {
 *    limit: 10,
 *    orderBy: [{ field: 'name', value: 'desc' }],
 *    where: [{ field: 'totalFollowers', operator: '>=', value: 100 }],
 *    startAfter: { ...data, id: 'z3Pdx...', path: 'users/z3Pdx...' },
 * });
 * console.log(usersData);
 * // [
 * //  { name: 'JOE DOE', ...data, id: 'Xq2mB...', path: 'users/Xq2mB...' },
 * //  { name: 'DEJA STEIN', ...data, id: 'Pz1Tl...', path: 'users/Pz1Tl...' }
 * // ]
 */
export async function getData({
  path,
  firestore,
  options = {},
  defaultData = {},
  formatter = (data) => data,
}) {
  try {
    const lastVisible = options.startAfter
      ? await getDoc(doc(firestore ?? FIRESTORE, options.startAfter.path))
      : null;

    const queryConstraints = [
      ...(options.where?.map(({ field, operator, value }) => where(field, operator, value)) ?? []),
      ...(options.orderBy?.map(({ field, value }) => orderBy(field, value)) ?? []),
      ...(lastVisible ? [startAfter(lastVisible)] : []),
      ...(options.limit ? [limit(options.limit)] : []),
    ];

    const fetchData =
      path && path.split('/').length % 2
        ? await getCollectionData(
            path,
            defaultData,
            formatter,
            queryConstraints,
            firestore ?? FIRESTORE
          )
        : await getDocData(path, defaultData, formatter, firestore ?? FIRESTORE);
    return fetchData;
  } catch (error) {
    console.error(error);
    return defaultData;
  }
}

export async function multipleQuery({
  path,
  firestore,
  searchValue,
  fields = [],
  mainFilters = [],
  defaultData = [],
  formatter = (data) => data,
}) {
  try {
    const filters = mainFilters.map(({ field, operator, value }) => where(field, operator, value));

    const queries = fields.map((field) =>
      query(collection(firestore ?? FIRESTORE, path), ...filters, where(field, '==', searchValue))
    );

    const results = await Promise.all(queries.map(getDocs));

    const fetchData = await Promise.all(
      results.flatMap(({ docs }) =>
        docs.map(async (document) => ({
          ...(await formatter({ id: document.id, ...document.data() })),
          id: document.id,
          path: document.ref.path,
        }))
      )
    );

    return fetchData;
  } catch (error) {
    console.error(error);
    return defaultData;
  }
}

/** Distributed counter */
export const distributedCounter = async (path, field, counter = 1, batch = null) => {
  await updateData(path, { [field]: increment(counter) }, batch);
};

export const customersCounter = async (sellerID, phoneNumber, creationDate, batch, firestore) => {
  const previousOrders = await getDocs(
    query(
      collection(firestore ?? FIRESTORE, 'orders'),
      where('sellerID', '==', sellerID),
      where('phoneNumber', '==', phoneNumber)
    )
  );

  if (!previousOrders.docs.length) {
    const [YEAR, MONTH, DAY] = creationDate.split('-');
    [
      `counters/${sellerID}/orders/${YEAR}`,
      `counters/${sellerID}/orders/${YEAR}/month/${MONTH}`,
      `counters/${sellerID}/orders/${YEAR}/month/${MONTH}/day/${DAY}`,
    ].map((path) => distributedCounter(path, 'customers', batch));
  }
};

/**
 * Counters from server
 * @input path: String, example: 'users' - only collection path
 * @input fieldSum: String, example: 'age'
 * @input fieldAverage: String, example: 'age'
 * @input options: Object, example: { limit: 10, startAfter: { path: 'users/${id}'}, where: [{ field: 'name', operator: '==', value: 'Joe' }], orderBy: [{ field: 'name', value: 'desc' }] }
 * @output Object { count: 10, sum: 150, average: 30 }
 * @see https://firebase.google.com/docs/firestore/query-data/aggregation-queries
 */
export const getDataSummary = async (path, fieldSum, fieldAverage, options, firestore) => {
  if (!path || !path.split('/').length % 2) return {};

  const queryConstraints = [
    ...(options.where?.map(({ field, operator, value }) => where(field, operator, value)) ?? []),
    ...(options.orderBy?.map(({ field, value }) => orderBy(field, value)) ?? []),
    ...(options.limit ? [limit(options.limit)] : []),
  ];

  const data = await getAggregateFromServer(
    query(collection(firestore ?? FIRESTORE, path), ...queryConstraints),
    {
      count: count(),
      ...(fieldSum && { sum: sum(fieldSum) }),
      ...(fieldAverage && { average: average(fieldAverage) }),
    }
  );

  return data.data();
};
